import { IPagination, IPaginationRequest } from '@simOn/common/http';
import { TransformationInterface } from '@simOn/common/matterport';
import { UserInfoCard } from '@simOn/common/user-info-card-models';
import { EnumValueGetter } from '@simOn/utils/models';
import type { TTicketStatus } from './ticket.models';

export const TaskTicketStatus = {
  Unknown: 0,
  Open: 10,
  InProgress: 50,
  Closed: 100
} as const;

export type TaskTicketStatusValue = EnumValueGetter<typeof TaskTicketStatus>;

export const TaskType = {
  Others: 0,
  Inspection: 1,
  Servicing: 2,
  RepairsAndFixes: 3,
  Upkeep: 4,
  Troubleshooting: 5,
  Monitoring: 6,
  SafetyChecks: 7,
  ComplianceChecks: 8,
  CleaningAndSanitization: 9
} as const;

export type TaskTypeValue = EnumValueGetter<typeof TaskType>;

export type TaskTypeName = keyof typeof TaskType;

export type TaskListStatus = TTicketStatus | TaskTicketStatusValue;

export type TaskListTaskType = TaskTypeName | TaskTypeValue;

export const TaskSortBy = {
  None: 0,
  Name: 1,
  DueDate: 2,
  CreatedAt: 3,
  EditedAt: 4,
  TaskType: 5
} as const;

export type TaskSortByValue = EnumValueGetter<typeof TaskSortBy>;

export interface TaskListEntry {
  id: string;
  name: string | null;
  apartmentId?: string;
  apartmentName?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  roomColor?: string | null;
  isMasterRoom?: boolean;
  status: TaskListStatus;
  taskType: TaskListTaskType;
  dueDate: string | null;
  dueTime: string | null;
  hasUpdates: boolean;
  positionDeleted: boolean;
  transformation?: TransformationInterface | null;
  stakeholders: UserInfoCard[] | null;
  createdAt: string;
  author: UserInfoCard | null;
  editedAt: string | null;
  editor: UserInfoCard | null;
}

export type TaskListPaginated = IPagination<TaskListEntry>;

export interface TaskListFilters {
  name?: string | null;
  onlyUnread?: boolean;
  statuses?: TaskListStatus[];
  authorIds?: string[];
  stakeholderIds?: string[];
  dueDateFrom?: string | null;
  dueDateTo?: string | null;
  editedAtFrom?: string | null;
  editedAtTo?: string | null;
  assignedOnly?: boolean;
  taskTypes?: TaskListTaskType[];
  apartmentIds?: string[];
  roomIds?: string[];
}

export interface TaskListFiltersWithPage extends TaskListFilters, IPaginationRequest {
  sortBy?: TaskSortByValue;
  isAscending?: boolean;
}

export interface TaskListFilterApartmentOption {
  apartmentId: string;
  apartmentName: string;
  isBlocked?: boolean;
}

export interface TaskListFilterRoomOption {
  roomId: string;
  roomName: string;
  roomColor: string | null;
  isMasterRoom: boolean;
}

export interface TaskListFilteringOptions {
  statuses: TaskListStatus[] | null;
  taskTypes: TaskListTaskType[] | null;
  authors: UserInfoCard[] | null;
  assignees: UserInfoCard[] | null;
  apartments: TaskListFilterApartmentOption[] | null;
  rooms: TaskListFilterRoomOption[] | null;
  dueDateFrom: string | null;
  dueDateTo: string | null;
  editedAtFrom: string | null;
  editedAtTo: string | null;
}

export const EMPTY_TASK_FILTERING_OPTIONS: TaskListFilteringOptions = {
  statuses: [],
  taskTypes: [],
  authors: [],
  assignees: [],
  apartments: [],
  rooms: [],
  dueDateFrom: null,
  dueDateTo: null,
  editedAtFrom: null,
  editedAtTo: null
};
